import { useEffect, useRef } from 'react';
import { getCountry, getMerchant, getHash, getStatus } from '@tectonics/prs/incoming-payment';
import type { IncomingPayment } from '@tectonics/prs/incoming-payment';
import { getPublicIntegrationKey, getName as getMerchantName, getLookAndFeel } from '@tectonics/prs/merchant';
import { sendGA4Event, buildCommonEventParams } from './send-ga4-event';

export function hashString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

export function useGA4Initialization(payment: IncomingPayment | null | undefined): void {
  const checkoutLoadedHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!payment || typeof window.gtag !== 'function') return;

    try {
      const merchant = getMerchant(payment);
      const paymentHash = getHash(payment);
      const hashedUserId = hashString(paymentHash);

      window.gtag('set', 'user_properties', {
        payment_hash: paymentHash,
        merchant_id: getPublicIntegrationKey(merchant),
        merchant_name: getMerchantName(merchant),
        checkout_country: getCountry(payment),
        payment_type_code: payment.payment_type_code || '',
        payment_status: getStatus(payment),
        look_and_feel: getLookAndFeel(merchant),
        app_environment: ENV_CONSTANTS.ENV,
        user_id: hashedUserId,
      });

      window.gtag('event', 'page_view', {
        page_title: `checkout_${getStatus(payment).toLowerCase()}`,
        page_location: window.location.href,
      });

      if (checkoutLoadedHashRef.current !== paymentHash) {
        const commonParams = buildCommonEventParams(payment);
        sendGA4Event('checkout_loaded', {
          ...commonParams,
          payment_type_code: payment.payment_type_code || '',
        });
        checkoutLoadedHashRef.current = paymentHash;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('GA4 initialization error:', e);
    }
  }, [payment?.hash, payment?.status]);
}
